import type {EventSocketMessage} from '@myparcel-frontend/event-service';
import type {events, Order} from '@myparcel-frontend/order-service';
import {matchOrderEvent} from '@myparcel-frontend/order-service';
import {createDebouncedEventQueue} from './createDebouncedEventQueue';
import useOrders from '../useOrders';

export default function useOrderServiceEvents() {
  const {
    bulkCsvImportSnapshot,
    addOrderToTopOfTheList,
    upsertOrder,
    getOrderByIdFromStore,
    removeOrder,
    handleOrderImportCompleted,
    handleOrderCsvImportCompleted,
    flagIncomingOrder,
  } = useOrders();

  const handleOrderCreatedEvent = (data: events.OrderCreatedEventValue): void => {
    const order = data;

    if (getOrderByIdFromStore(order.id)) {
      return;
    }

    flagIncomingOrder(order);
    addOrderToTopOfTheList({...order});
  };

  const handleOrderDeletedEvent = (data: events.OrderUpdatedEventValue) => removeOrder(data.id);

  const handleOrderUpdatedEvent = (data: events.OrderUpdatedEventValue) => {
    // Flag before the upsert so the order's previous status is still in the store for comparison.
    flagIncomingOrder(data);

    // CSV-import snapshot items are keyed by the ShopOrder sourceId, not the order-service id.
    if (data.externalReferences?.source === 'IMPORT') {
      bulkCsvImportSnapshot.updateItemAfterProp(data.externalReferences.sourceId, data);
    }

    upsertOrder({...data});
  };

  const handleOrderRulesEvaluatedEvent = (data: Order) => {
    const isCsvImport = data.externalReferences?.source === 'IMPORT';

    if (isCsvImport) {
      handleOrderCsvImportCompleted(data);
    } else {
      handleOrderImportCompleted(data);
    }

    flagIncomingOrder(data);
    upsertOrder({...data});
  };

  const dispatchOrderEvent = (event: EventSocketMessage): void =>
    matchOrderEvent(event, {
      onCreated: handleOrderCreatedEvent,
      onUpdated: handleOrderUpdatedEvent,
      onDeleted: handleOrderDeletedEvent,
      onRuleEvaluated: handleOrderRulesEvaluatedEvent,
    });

  // Order events can arrive in bursts (e.g. a CSV import); batch them so the
  // store isn't updated on every single message.
  const handleOrderMessage = createDebouncedEventQueue(dispatchOrderEvent);

  return {handleOrderMessage};
}
